import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  AvatarGroup,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Drawer,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { teamAPI, userAPI } from '../services/api';
import { Team, User } from '../types';

const TeamManagement: React.FC = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  // Member drawer
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);
  const [drawerTeam, setDrawerTeam] = useState<Team | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState('');
  const [drawerAddUser, setDrawerAddUser] = useState<User | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: '',
    members: [] as string[],
    teamLead: '' as string | undefined,
    additionalManagers: [] as string[],
  });
  // Helpers for create/edit
  const [memberSearch, setMemberSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await teamAPI.getTeams();
      setTeams(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const openMemberDrawer = async (team: Team) => {
    setDrawerTeam(team);
    setMemberDrawerOpen(true);
  };

  const handleAddMember = async () => {
    if (!drawerTeam || !drawerAddUser) return;
    try {
      setDrawerLoading(true);
      await teamAPI.addMember(drawerTeam._id, drawerAddUser._id);
      const updated = await teamAPI.getTeam(drawerTeam._id);
      setDrawerTeam(updated.data);
      setDrawerAddUser(null);
      fetchTeams();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to add member');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!drawerTeam) return;
    try {
      setDrawerLoading(true);
      await teamAPI.removeMember(drawerTeam._id, userId);
      const updated = await teamAPI.getTeam(drawerTeam._id);
      setDrawerTeam(updated.data);
      fetchTeams();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to remove member');
    } finally {
      setDrawerLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleOpenCreate = () => {
    setDialogMode('create');
    setFormData({
      name: '',
      description: '',
      department: '',
      members: [],
      teamLead: undefined,
      additionalManagers: [],
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (team: Team) => {
    setDialogMode('edit');
    setSelectedTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      department: team.department || '',
      members: Array.isArray(team.members) 
        ? team.members.map((m: any) => typeof m === 'object' ? m._id : m)
        : [],
      teamLead: (team as any).teamLead ? (typeof (team as any).teamLead === 'string' ? (team as any).teamLead : (team as any).teamLead._id) : undefined,
      additionalManagers: (team as any).additionalManagers ? (team as any).additionalManagers.map((m: any) => typeof m === 'string' ? m : m._id) : [],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTeam(null);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleMemberToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter(id => id !== userId)
        : [...prev.members, userId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (dialogMode === 'create') {
        await teamAPI.createTeam(formData);
        setSuccess('Team created successfully!');
      } else if (selectedTeam) {
        await teamAPI.updateTeam(selectedTeam._id, formData);
        setSuccess('Team updated successfully!');
      }
      
      handleCloseDialog();
      fetchTeams();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${dialogMode} team`);
    }
  };

  const handleDeleteClick = (team: Team) => {
    setTeamToDelete(team);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!teamToDelete) return;

    try {
      await teamAPI.deleteTeam(teamToDelete._id);
      setSuccess('Team deleted successfully!');
      setDeleteConfirmOpen(false);
      setTeamToDelete(null);
      fetchTeams();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete team');
      setDeleteConfirmOpen(false);
    }
  };

  const getMemberCount = (team: Team) => {
    if (!Array.isArray(team.members)) return 0;
    const ids = team.members.map((m: any) => (typeof m === 'string' ? m : m._id));
    return new Set(ids).size;
  };

  const getTeamMembers = (team: Team) => {
    if (!Array.isArray(team.members)) return [];
    return team.members.slice(0, 3); // Show first 3 members
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Team Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Team
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {teams.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No teams yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first team to organize employees
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Create Team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {teams.map((team) => (
            <Card key={team._id}>
              <CardContent
                onClick={() => navigate(`/teams/${team._id}`)}
                sx={{ cursor: 'pointer' }}
                title="Click to view team members"
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {team.name}
                  </Typography>
                  <Chip 
                    label={team.isActive ? 'Active' : 'Inactive'} 
                    color={team.isActive ? 'success' : 'default'} 
                    size="small" 
                  />
                </Box>
                
                {team.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {team.description}
                  </Typography>
                )}
                
                {team.department && (
                  <Chip label={team.department} size="small" sx={{ mb: 2 }} />
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {getMemberCount(team)} members
                  </Typography>
                </Box>
                
                {getMemberCount(team) > 0 && (
                  <AvatarGroup max={4} sx={{ justifyContent: 'flex-start', mt: 2 }}>
                    {getTeamMembers(team).map((member: any) => (
                      <Avatar key={member._id} sx={{ width: 32, height: 32 }}>
                        {member.name?.charAt(0)}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEdit(team)}>
                  Edit
                </Button>
                <Button size="small" onClick={() => openMemberDrawer(team)}>Manage Members</Button>
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick(team)}>
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Team' : 'Edit Team'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Team Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Team Members
            </Typography>
            <TextField
              fullWidth
              value={memberSearch}
              onChange={(e) => { setMemberSearch(e.target.value); setPage(0); }}
              placeholder="Search employees by name or email"
              size="small"
              sx={{ mb: 1 }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No employees available
                </Typography>
              ) : (
                <List dense>
                  {users
                    .filter((u) => u.role === 'employee')
                    .filter((u) =>
                      (u.name || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
                      (u.email || '').toLowerCase().includes(memberSearch.toLowerCase())
                    )
                    .slice(page * pageSize, page * pageSize + pageSize)
                    .map((user) => (
                    <ListItem key={user._id} button onClick={() => handleMemberToggle(user._id)}>
                      <ListItemAvatar>
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name}
                        secondary={user.email}
                      />
                      <ListItemSecondaryAction>
                        <Checkbox
                          edge="end"
                          checked={formData.members.includes(user._id)}
                          onChange={() => handleMemberToggle(user._id)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</Button>
              <Button size="small" onClick={() => setPage((p) => p + 1)}>Next</Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {formData.members.length} member(s) selected
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Team Lead (optional)
            </Typography>
            <Autocomplete
              options={users.filter((u) => formData.members.includes(u._id))}
              getOptionLabel={(o) => o.name}
              value={users.find((u) => u._id === formData.teamLead) || null}
              onChange={(_, val) => setFormData((prev) => ({ ...prev, teamLead: val?._id }))}
              renderInput={(params) => <TextField {...params} placeholder="Select a team lead" />}
            />

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Additional Managers (optional)
            </Typography>
            <Autocomplete
              multiple
              options={users.filter((u) => u.role === 'manager' || u.role === 'admin')}
              getOptionLabel={(o) => `${o.name} (${o.role})`}
              value={users.filter((u) => formData.additionalManagers.includes(u._id))}
              onChange={(_, val) => setFormData((prev) => ({ ...prev, additionalManagers: val.map((v) => v._id) }))}
              renderInput={(params) => <TextField {...params} placeholder="Select additional managers" />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Drawer */}
      <Drawer anchor="right" open={memberDrawerOpen} onClose={() => setMemberDrawerOpen(false)}>
        <Box sx={{ width: 320, p: 2 }}>
          <Typography variant="h6" gutterBottom>Manage Members</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{drawerTeam?.name}</Typography>

          <TextField
            fullWidth
            label="Search employees by name or email"
            value={memberSearch}
            onChange={(e) => { setMemberSearch(e.target.value); setPage(0); }}
            size="small"
            sx={{ mb: 1 }}
          />

          <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
            {users.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No employees available
              </Typography>
            ) : (
              <List dense>
                {users
                  .filter((u) => u.role === 'employee')
                  .filter((u) =>
                    (u.name || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
                    (u.email || '').toLowerCase().includes(memberSearch.toLowerCase())
                  )
                  .slice(page * pageSize, page * pageSize + pageSize)
                  .map((u) => {
                    const isMember = (drawerTeam?.members as any[] | undefined)?.some((m: any) => (typeof m === 'string' ? m : m._id) === u._id);
                    return (
                      <ListItem key={u._id}>
                        <ListItemAvatar>
                          <Avatar><PersonIcon /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={u.name} secondary={u.email} />
                        <ListItemSecondaryAction>
                          <Checkbox
                            edge="end"
                            checked={!!isMember}
                            onChange={() => (
                              isMember
                                ? handleRemoveMember(u._id)
                                : (setDrawerAddUser(u), handleAddMember())
                            )}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

    </Container>
  );
};

export default TeamManagement;
